class Resources {
    constructor() {
        this.workInProgress = 0;
        this.money = 0;
        this.reputation = 0;
        this.influence = 0; // Political currency for end-game
    }
}

class OfficeGame {
    constructor() {
        this.resources = new Resources();
        this.upgrades = [];
        this.currentTask = null;
        this.level = 1;
        this.productivityMultiplier = 1.0;
        this.lastUpdate = Date.now();
        this.storyProgress = 0;
        this.currentDialogue = null;
        this.dialogueHandled = false;
        this.dialogueQueue = [];
        this.completedStoryEvents = new Set(); // Track which story events have been shown
        this.gameComplete = false; // Game ending state

        // Initialize stats
        this.stats = {
            tasksCompleted: 0,
            taskCompletions: {},
            coffeeTaken: 0
        };
        
        // Track relationships
        this.relationships = {
            supervisor: 0,
            hrManager: 0,
            colleagues: {}
        };

        // Automation systems
        this.autoClicker = {
            enabled: false,
            active: true, // User toggle - allows disabling auto-clicker
            clicksPerSecond: 0,
            level: 0
        };

        this.emailAutoResponder = {
            enabled: false,
            active: true, // User toggle - allows disabling auto email start
            level: 0
        };

        this.taskSchedulerActive = true; // User toggle for task scheduler

        // Employees/Interns system
        this.employees = [];
        this.employeeTypes = [
            { id: 'intern', name: 'Intern', baseCost: 50, taskEfficiency: 0.3, description: 'Works slowly but learns fast' },
            { id: 'junior', name: 'Junior Employee', baseCost: 200, taskEfficiency: 0.6, description: 'Handles basic tasks efficiently' },
            { id: 'senior', name: 'Senior Employee', baseCost: 500, taskEfficiency: 1.0, description: 'Expert worker, full efficiency' },
            { id: 'manager', name: 'Manager', baseCost: 1500, taskEfficiency: 1.5, description: 'Boosts all employee output by 20%' }
        ];

        // Prestige/Promotion system
        this.prestige = {
            level: 0,
            title: 'New Hire',
            totalPromotions: 0,
            permanentBonuses: {
                clickMultiplier: 1.0,
                moneyMultiplier: 1.0,
                reputationMultiplier: 1.0,
                employeeEfficiency: 1.0
            }
        };

        this.promotionTitles = [
            { level: 0, title: 'New Hire', requirement: 0 },
            { level: 1, title: 'Associate', requirement: 100 },
            { level: 2, title: 'Senior Associate', requirement: 300 },
            { level: 3, title: 'Team Lead', requirement: 600 },
            { level: 4, title: 'Manager', requirement: 1000 },
            { level: 5, title: 'Senior Manager', requirement: 2000 },
            { level: 6, title: 'Director', requirement: 4000 },
            { level: 7, title: 'VP', requirement: 8000 },
            { level: 8, title: 'SVP', requirement: 15000 },
            { level: 9, title: 'C-Suite', requirement: 30000 },
            { level: 10, title: 'CEO', requirement: 60000 }
        ];

        // Politics system (end-game)
        this.politics = {
            enabled: false,
            factions: {
                management: { favor: 50, name: 'Management' },  // Start at 50 (neutral)
                workers: { favor: 50, name: 'Workers Union' },
                board: { favor: 50, name: 'Board of Directors' }
            },
            currentScheme: null,
            completedSchemes: [],
            powerPlays: 0,
            // Dilemma system
            currentDilemma: null,
            completedDilemmas: [],
            dilemmaTimer: 0,  // Time until next dilemma
            dilemmaInterval: 30,  // Seconds between dilemmas (after tasks/promotions)
            gameOver: false,  // True if a faction dropped too low
            gameOverReason: null
        };

        this.politicalSchemes = [
            { id: 'coffee_alliance', name: 'Coffee Machine Alliance', cost: 50, influenceGain: 10, faction: 'workers', requirement: { reputation: 15 }, description: 'Bond over the coffee machine' },
            { id: 'budget_proposal', name: 'Budget Proposal', cost: 100, influenceGain: 25, faction: 'management', requirement: { reputation: 25 }, description: 'Submit a cost-saving proposal' },
            { id: 'mentorship', name: 'Mentorship Program', cost: 150, influenceGain: 30, faction: 'workers', requirement: { employees: 2 }, description: 'Train the newcomers' },
            { id: 'board_presentation', name: 'Board Presentation', cost: 300, influenceGain: 50, faction: 'board', requirement: { reputation: 40 }, description: 'Present quarterly results' },
            { id: 'restructuring', name: 'Restructuring Plan', cost: 500, influenceGain: 75, faction: 'management', requirement: { influence: 50 }, description: 'Propose department changes' },
            { id: 'union_negotiation', name: 'Union Negotiation', cost: 400, influenceGain: 60, faction: 'workers', requirement: { influence: 40 }, description: 'Mediate worker disputes' },
            { id: 'merger_support', name: 'Merger Support', cost: 800, influenceGain: 100, faction: 'board', requirement: { influence: 80 }, description: 'Advocate for strategic merger' },
            { id: 'executive_dinner', name: 'Executive Dinner', cost: 1000, influenceGain: 120, faction: 'board', requirement: { prestige: 5 }, description: 'Dine with the executives' },
            { id: 'hostile_takeover', name: 'Hostile Takeover Defense', cost: 2000, influenceGain: 200, faction: 'board', requirement: { prestige: 7 }, description: 'Protect the company' },
            { id: 'ceo_succession', name: 'CEO Succession Plan', cost: 5000, influenceGain: 500, faction: 'board', requirement: { prestige: 9 }, description: 'Position yourself for the top' }
        ];

        // Political dilemmas - binary choices that affect faction favor (Reigns-style)
        this.politicalDilemmas = [
            // Early dilemmas (prestige 3-4)
            {
                id: 'overtime_request',
                text: "The Workers Union is pushing back on mandatory overtime. Management wants you to enforce it.",
                choiceA: { text: "Enforce overtime - productivity matters", effects: { management: 15, workers: -20 } },
                choiceB: { text: "Support work-life balance", effects: { workers: 15, management: -15 } },
                minPrestige: 3
            },
            {
                id: 'budget_cuts',
                text: "The Board wants to cut the coffee budget. Workers are upset about losing their perk.",
                choiceA: { text: "Cut the budget - savings first", effects: { board: 10, workers: -15 } },
                choiceB: { text: "Keep the coffee - morale matters", effects: { workers: 10, board: -10 } },
                minPrestige: 3
            },
            {
                id: 'promotion_candidate',
                text: "Two candidates for a promotion: Management's favorite or the Workers' popular choice.",
                choiceA: { text: "Promote Management's pick", effects: { management: 15, workers: -10 } },
                choiceB: { text: "Promote the popular employee", effects: { workers: 15, management: -10 } },
                minPrestige: 3
            },
            {
                id: 'remote_work',
                text: "Workers want more remote work days. Management prefers in-office presence.",
                choiceA: { text: "Mandate office attendance", effects: { management: 10, workers: -15 } },
                choiceB: { text: "Allow flexible remote work", effects: { workers: 15, management: -10, board: -5 } },
                minPrestige: 3
            },
            {
                id: 'training_budget',
                text: "Use the training budget for executive coaching or worker skill development?",
                choiceA: { text: "Executive coaching program", effects: { management: 15, board: 5, workers: -10 } },
                choiceB: { text: "Worker development program", effects: { workers: 20, management: -10 } },
                minPrestige: 3
            },
            // Mid-game dilemmas (prestige 4-6)
            {
                id: 'layoff_proposal',
                text: "The Board is considering layoffs to boost quarterly numbers.",
                choiceA: { text: "Support the layoffs", effects: { board: 20, workers: -25, management: 5 } },
                choiceB: { text: "Propose alternatives", effects: { workers: 15, board: -15, management: -5 } },
                minPrestige: 4
            },
            {
                id: 'union_meeting',
                text: "Workers want to discuss forming a stronger union. Management is nervous.",
                choiceA: { text: "Discourage union talks", effects: { management: 20, board: 10, workers: -25 } },
                choiceB: { text: "Support open dialogue", effects: { workers: 25, management: -20, board: -10 } },
                minPrestige: 4
            },
            {
                id: 'expense_report',
                text: "You discover a manager padding expense reports. Do you report it to the Board?",
                choiceA: { text: "Report the misconduct", effects: { board: 20, workers: 10, management: -25 } },
                choiceB: { text: "Handle it quietly", effects: { management: 15, board: -15, workers: -5 } },
                minPrestige: 4
            },
            {
                id: 'holiday_party',
                text: "Holiday party budget: lavish executive event or company-wide celebration?",
                choiceA: { text: "Exclusive executive dinner", effects: { board: 15, management: 10, workers: -15 } },
                choiceB: { text: "Party for everyone", effects: { workers: 20, board: -10, management: -5 } },
                minPrestige: 5
            },
            {
                id: 'project_credit',
                text: "A successful project - let management take credit or highlight the team effort?",
                choiceA: { text: "Credit the leadership", effects: { management: 15, board: 10, workers: -15 } },
                choiceB: { text: "Credit the whole team", effects: { workers: 20, management: -15 } },
                minPrestige: 5
            },
            // Late-game dilemmas (prestige 6-8)
            {
                id: 'acquisition_target',
                text: "The Board wants to acquire a competitor. It would mean layoffs from overlap.",
                choiceA: { text: "Support the acquisition", effects: { board: 25, management: 10, workers: -20 } },
                choiceB: { text: "Advocate against it", effects: { workers: 15, board: -25, management: -10 } },
                minPrestige: 6
            },
            {
                id: 'whistleblower',
                text: "An employee wants to report safety violations. Management wants to suppress it.",
                choiceA: { text: "Suppress the report", effects: { management: 20, board: 10, workers: -30 } },
                choiceB: { text: "Support the whistleblower", effects: { workers: 25, management: -25, board: -15 } },
                minPrestige: 6
            },
            {
                id: 'dividend_vs_raises',
                text: "Record profits this quarter. The Board wants dividends, workers want raises.",
                choiceA: { text: "Prioritize dividends", effects: { board: 25, workers: -25 } },
                choiceB: { text: "Prioritize raises", effects: { workers: 30, board: -25, management: 5 } },
                minPrestige: 7
            },
            {
                id: 'ceo_scandal',
                text: "The CEO is involved in a scandal. The Board is divided on how to respond.",
                choiceA: { text: "Protect the CEO", effects: { management: 20, board: -20, workers: -15 } },
                choiceB: { text: "Call for accountability", effects: { workers: 20, board: 15, management: -25 } },
                minPrestige: 7
            },
            {
                id: 'office_relocation',
                text: "Relocate HQ to a cheaper area? The Board loves the savings, workers hate the commute.",
                choiceA: { text: "Approve relocation", effects: { board: 30, management: 10, workers: -30 } },
                choiceB: { text: "Stay put", effects: { workers: 20, board: -20 } },
                minPrestige: 8
            },
            // End-game dilemmas (prestige 8-9)
            {
                id: 'golden_parachute',
                text: "Executives want golden parachutes in their contracts. Workers call it unfair.",
                choiceA: { text: "Approve the packages", effects: { management: 25, board: 15, workers: -25 } },
                choiceB: { text: "Reject as excessive", effects: { workers: 25, management: -30, board: -10 } },
                minPrestige: 8
            },
            {
                id: 'automation_plan',
                text: "Automate key processes? Huge efficiency gains, but many jobs will be eliminated.",
                choiceA: { text: "Full automation ahead", effects: { board: 30, management: 15, workers: -35 } },
                choiceB: { text: "Gradual transition with retraining", effects: { workers: 25, board: -15, management: -5 } },
                minPrestige: 8
            },
            {
                id: 'succession_politics',
                text: "The CEO is retiring. Position yourself or support the Board's preferred candidate?",
                choiceA: { text: "Support the Board's choice", effects: { board: 35, management: -15, workers: -10 } },
                choiceB: { text: "Campaign for yourself", effects: { workers: 20, management: 15, board: -25 } },
                minPrestige: 9
            },
            {
                id: 'final_restructure',
                text: "Major restructuring proposal: lean management or worker-focused culture?",
                choiceA: { text: "Lean and efficient", effects: { board: 30, management: 20, workers: -30 } },
                choiceB: { text: "Worker-first culture", effects: { workers: 35, board: -25, management: -15 } },
                minPrestige: 9
            }
        ];

        // Initialize achievements
        this.achievements = new Set();
        this.achievementsList = [
            { id: "first_task", name: "First Day", description: "Complete your first task", reward: 5 },
            { id: "busy_worker", name: "Busy Bee", description: "Complete 5 tasks", reward: 15 },
            { id: "social_butterfly", name: "Social Butterfly", description: "Take a coffee break", reward: 10 },
            { id: "team_player", name: "Team Player", description: "Help IT with spreadsheets", reward: 20 },
            { id: "phone_pro", name: "Phone Pro", description: "Answer 3 phone calls", reward: 15 },
            { id: "meeting_master", name: "Meeting Master", description: "Prepare 2 meeting notes", reward: 20 },
            { id: "report_writer", name: "Report Writer", description: "Write your first report", reward: 25 },
            { id: "trained_employee", name: "Trained Employee", description: "Complete training", reward: 30 },
            { id: "workaholic", name: "Workaholic", description: "Complete 10 tasks", reward: 25 },
            { id: "office_veteran", name: "Office Veteran", description: "Complete 15 tasks", reward: 50 },
            { id: "money_maker", name: "Money Maker", description: "Earn $100", reward: 20 },
            { id: "respected", name: "Respected", description: "Reach 10 reputation", reward: 30 }
        ];

        this.initializeGame();
        
        // Ensure first dialogue is set
        if (this.dialogueQueue.length > 0) {
            this.currentDialogue = this.dialogueQueue.shift();
            this.dialogueHandled = false;
        }
    }

    initializeGame() {
        // Story sequences - extended narrative
        this.storyEvents = [
            {
                id: 'welcome',
                character: 'Sarah (HR)',
                text: "Welcome to your first day! I'm Sarah from HR. Let me show you to your desk.",
                choices: [
                    { text: "Thanks, I'm excited to start!", effect: { hrManager: 1 } },
                    { text: "Thanks, I'll find my way.", effect: { hrManager: 0 } }
                ]
            },
            {
                id: 'supervisor',
                character: 'Mark (Supervisor)',
                text: "Hi, I'm Mark, your supervisor. Let's start with something simple - checking your emails.",
                choices: [
                    { text: "I'm ready to learn!", effect: { supervisor: 1 } },
                    { text: "Okay, sounds easy enough.", effect: { supervisor: 0 } }
                ],
                unlocks: { task: 'email' }
            },
            {
                id: 'filing_complete',
                character: 'Mark (Supervisor)',
                text: "Good job with the filing. Let me introduce you to Amy, she'll be working with you.",
                choices: [
                    { text: "Looking forward to meeting her", effect: { supervisor: 1 } },
                    { text: "Alright, thanks", effect: { supervisor: 0 } }
                ]
            },
            {
                id: 'coffee_intro',
                character: 'Amy (Colleague)',
                text: "Hey! I'm Amy from the next cubicle. Want to grab coffee sometime?",
                choices: [
                    { text: "Sure, that'd be great!", effect: { colleagues: { amy: 1 }, unlocks: 'coffee' } },
                    { text: "Maybe later, busy now.", effect: { colleagues: { amy: 0 } } }
                ],
                requirement: { tasksCompleted: 2 }
            },
            {
                id: 'after_coffee',
                character: 'Amy (Colleague)',
                text: "That was fun! By the way, I heard IT needs help with some spreadsheet work. Want me to put in a good word?",
                choices: [
                    { text: "Yes, please! I'd love to help.", effect: { colleagues: { amy: 1 }, unlocks: 'spreadsheet' } },
                    { text: "I should focus on current tasks.", effect: { colleagues: { amy: 0 } } }
                ],
                requirement: { coffeeTaken: true }
            },
            {
                id: 'it_intro',
                character: 'Tom (IT)',
                text: "Amy mentioned you could help. We need these computer usage logs organized in a spreadsheet.",
                choices: [
                    { text: "I'll get right on it!", effect: { colleagues: { tom: 1 } } },
                    { text: "Sure, I can handle that.", effect: { colleagues: { tom: 0 } } }
                ],
                requirement: { taskUnlocked: 'spreadsheet' }
            },
            {
                id: 'spreadsheet_complete',
                character: 'Tom (IT)',
                text: "Great work on those logs! I'll put in a good word with your supervisor.",
                choices: [
                    { text: "Thanks, I enjoyed helping out!", effect: { colleagues: { tom: 1 }, reputation: 5 } },
                    { text: "No problem.", effect: { colleagues: { tom: 0 }, reputation: 2 } }
                ],
                requirement: { taskCompletions: { spreadsheet: 1 } }
            },
            {
                id: 'phone_intro',
                character: 'Mark (Supervisor)',
                text: "You're settling in well! Ready to start handling some phone calls from customers?",
                choices: [
                    { text: "Absolutely, I can handle that!", effect: { supervisor: 1, unlocks: 'phone' } },
                    { text: "I'll give it a try.", effect: { supervisor: 0, unlocks: 'phone' } }
                ],
                requirement: { tasksCompleted: 4 }
            },
            {
                id: 'desk_upgrade',
                character: 'Amy (Colleague)',
                text: "Hey, I noticed your desk is pretty bare. Want to grab a plant from the break room? They really boost productivity!",
                choices: [
                    { text: "Great idea! A plant would be nice.", effect: { colleagues: { amy: 1 }, unlocks: 'desk_plant' } },
                    { text: "Maybe later, I'm focused on work.", effect: { colleagues: { amy: 0 } } }
                ],
                requirement: { tasksCompleted: 5 }
            },
            {
                id: 'meeting_intro',
                character: 'Mark (Supervisor)',
                text: "We have team meetings every week. Can you start preparing the meeting notes and agenda?",
                choices: [
                    { text: "I'd be happy to help organize!", effect: { supervisor: 1, unlocks: 'meeting' } },
                    { text: "Sure, I can do that.", effect: { supervisor: 0, unlocks: 'meeting' } }
                ],
                requirement: { tasksCompleted: 6 }
            },
            {
                id: 'energy_drink',
                character: 'Tom (IT)',
                text: "Working late? I keep energy drinks in the vending machine. They really help with the afternoon slump!",
                choices: [
                    { text: "Thanks for the tip!", effect: { colleagues: { tom: 1 }, unlocks: 'energy_drink' } },
                    { text: "I'll stick to coffee.", effect: { colleagues: { tom: 0 } } }
                ],
                requirement: { tasksCompleted: 7 }
            },
            {
                id: 'training_intro',
                character: 'Sarah (HR)',
                text: "Hi! Just a reminder - you need to complete the mandatory HR training module. It's company policy.",
                choices: [
                    { text: "I'll get it done right away!", effect: { hrManager: 1, unlocks: 'training' } },
                    { text: "Okay, I'll add it to my list.", effect: { hrManager: 0, unlocks: 'training' } }
                ],
                requirement: { tasksCompleted: 8 }
            },
            {
                id: 'chair_upgrade',
                character: 'Mark (Supervisor)',
                text: "I noticed you've been working hard. HR approved a budget for ergonomic equipment. Want a new chair?",
                choices: [
                    { text: "That would be amazing, thank you!", effect: { supervisor: 1, unlocks: 'ergonomic_chair' } },
                    { text: "Sure, that sounds useful.", effect: { supervisor: 0, unlocks: 'ergonomic_chair' } }
                ],
                requirement: { tasksCompleted: 9 }
            },
            {
                id: 'report_intro',
                character: 'Mark (Supervisor)',
                text: "Great progress! I think you're ready to take on the weekly status report. It goes to senior management.",
                choices: [
                    { text: "I'm honored! I won't let you down.", effect: { supervisor: 1, unlocks: 'report' } },
                    { text: "I'll do my best.", effect: { supervisor: 0, unlocks: 'report' } }
                ],
                requirement: { tasksCompleted: 10 }
            },
            {
                id: 'lunch_intro',
                character: 'Amy (Colleague)',
                text: "The team is doing a group lunch this Friday! It's a great way to build relationships. Want to join?",
                choices: [
                    { text: "Count me in!", effect: { colleagues: { amy: 1 }, unlocks: 'lunch_break' } },
                    { text: "I'll think about it.", effect: { colleagues: { amy: 0 } } }
                ],
                requirement: { tasksCompleted: 11 }
            },
            {
                id: 'first_week_complete',
                character: 'Mark (Supervisor)',
                text: "You've done outstanding work this week! I'm impressed with how quickly you've adapted. You're definitely going places.",
                choices: [
                    { text: "Thanks, I'm really enjoying it here!", effect: { supervisor: 1 } },
                    { text: "Thanks for the opportunity.", effect: { supervisor: 0 } }
                ],
                requirement: { tasksCompleted: 12 }
            },
            // Week 2 events
            {
                id: 'week_two_start',
                character: 'Mark (Supervisor)',
                text: "Ready for week two? We've got some bigger projects coming up. I think you're ready to take on more responsibility.",
                choices: [
                    { text: "Bring it on!", effect: { supervisor: 1 } },
                    { text: "I'll do my best.", effect: { supervisor: 0 } }
                ],
                requirement: { tasksCompleted: 15 }
            },
            {
                id: 'promotion_hint',
                character: 'Amy (Colleague)',
                text: "Word around the office is that Mark's been talking you up to the higher-ups. Keep up the good work!",
                choices: [
                    { text: "That's great to hear!", effect: { colleagues: { amy: 1 } } },
                    { text: "I'm just doing my job.", effect: { colleagues: { amy: 0 } } }
                ],
                requirement: { tasksCompleted: 18 }
            },
            {
                id: 'week_two_complete',
                character: 'Mark (Supervisor)',
                text: "Two weeks down and you're already one of our top performers! The team really appreciates your dedication.",
                choices: [
                    { text: "This team makes it easy!", effect: { supervisor: 1, colleagues: { amy: 1, bob: 1 } } },
                    { text: "Happy to contribute.", effect: { supervisor: 0 } }
                ],
                requirement: { tasksCompleted: 24 }
            },
            // Week 3+ events
            {
                id: 'month_milestone',
                character: 'Mark (Supervisor)',
                text: "Can you believe it's almost been a month? HR wants to do your official performance review soon. I'll put in a good word.",
                choices: [
                    { text: "I appreciate that!", effect: { supervisor: 1 } },
                    { text: "Looking forward to it.", effect: { supervisor: 0 } }
                ],
                requirement: { tasksCompleted: 36 }
            },
            {
                id: 'office_veteran',
                character: 'Bob (IT)',
                text: "You know, you've really become part of the furniture around here. In a good way! Let me know if you ever need any tech favors.",
                choices: [
                    { text: "Thanks Bob, you're the best!", effect: { colleagues: { bob: 1 } } },
                    { text: "I'll keep that in mind.", effect: { colleagues: { bob: 0 } } }
                ],
                requirement: { tasksCompleted: 48 }
            },
            // Politics Introduction - when reaching Team Lead
            {
                id: 'politics_intro',
                character: 'Mark (Supervisor)',
                text: "Congratulations on making Team Lead! Now comes the real challenge. At this level, you'll face political dilemmas. Management, Workers, and the Board all have competing interests. You'll need to balance them carefully.",
                choices: [
                    { text: "I'm ready for the challenge!", effect: { supervisor: 1 } },
                    { text: "Sounds complicated...", effect: { supervisor: 0 } }
                ],
                requirement: { prestige: 3 }
            },
            {
                id: 'politics_warning',
                character: 'Mark (Supervisor)',
                text: "A word of advice: keep all three factions above 20 favor. If any faction turns hostile, your career here is over. But balance them well, and you might just make it to CEO.",
                choices: [
                    { text: "Got it - balance is key!", effect: { supervisor: 1 } },
                    { text: "I'll do my best.", effect: { supervisor: 0 } }
                ],
                requirement: { politicsIntroSeen: true }
            },
            // Late-game milestone story events
            {
                id: 'director_challenge',
                character: 'Mark (Supervisor)',
                text: "Director, huh? The view from up here is different. Every decision you make now ripples through the whole company. The factions are watching your every move more closely than ever.",
                choices: [
                    { text: "I thrive under pressure.", effect: { supervisor: 1 } },
                    { text: "It's lonely at the top.", effect: { supervisor: 1 } }
                ],
                requirement: { prestige: 6 }
            },
            {
                id: 'vp_politics',
                character: 'Board of Directors',
                text: "As VP, you now have a seat at the executive table. The Board expects quarterly strategy reports. Your faction relationships will be scrutinized at every board meeting.",
                choices: [
                    { text: "I'll deliver results.", effect: { supervisor: 0 } },
                    { text: "Time to play the long game.", effect: { supervisor: 0 } }
                ],
                requirement: { prestige: 7 }
            },
            {
                id: 'svp_succession',
                character: 'Amy (Colleague)',
                text: "The rumor mill says you're on the short list for the CEO succession. People are already picking sides. Be careful who you trust from here on out.",
                choices: [
                    { text: "Thanks for the heads up.", effect: { colleagues: { amy: 2 } } },
                    { text: "I'll keep my head down.", effect: { colleagues: { amy: 1 } } }
                ],
                requirement: { prestige: 8 }
            },
            {
                id: 'csuite_final_push',
                character: 'Mark (Supervisor)',
                text: "You're C-Suite now. One more step and you'll be running this place. The CEO is watching — every faction needs to believe you're their champion. This is the final push.",
                choices: [
                    { text: "I was born for this.", effect: { supervisor: 2 } },
                    { text: "Let's finish what we started.", effect: { supervisor: 1 } }
                ],
                requirement: { prestige: 9 }
            },
            // CEO Victory Event
            {
                id: 'ceo_victory',
                character: 'Board of Directors',
                text: "Congratulations! After an extraordinary rise through the ranks, the Board of Directors has unanimously voted to appoint you as the new CEO. Your journey from New Hire to Chief Executive Officer is complete. You've reached the top!",
                choices: [
                    { text: "I'm honored to lead this company!", effect: { endGame: true } },
                    { text: "This is just the beginning.", effect: { endGame: true } }
                ],
                requirement: { prestige: 10 }
            }
        ];

        // Start with just email task
        this.tasks = [
            {
                id: "email",
                name: "Check Emails",
                workNeeded: 5,
                reward: 10,
                description: "Read through your orientation emails",
                locked: true
            },
            {
                id: "filing",
                name: "File Documents",
                workNeeded: 10,
                reward: 20,
                description: "Organize the new hire paperwork",
                locked: true
            },
            {
                id: "spreadsheet",
                name: "Organize IT Logs",
                workNeeded: 15,
                reward: 30,
                description: "Create a spreadsheet of computer usage logs",
                locked: true
            },
            {
                id: "phone",
                name: "Answer Phone Calls",
                workNeeded: 8,
                reward: 15,
                description: "Handle incoming customer inquiries",
                locked: true
            },
            {
                id: "meeting",
                name: "Prepare Meeting Notes",
                workNeeded: 12,
                reward: 25,
                description: "Prepare agenda and notes for team meeting",
                locked: true
            },
            {
                id: "report",
                name: "Write Weekly Report",
                workNeeded: 20,
                reward: 40,
                description: "Compile and write the weekly status report",
                locked: true
            },
            {
                id: "training",
                name: "Complete Training Module",
                workNeeded: 25,
                reward: 50,
                description: "Finish the mandatory HR training course",
                locked: true
            }
        ];

        // Upgrades are unlocked through story progression
        this.upgrades = [
            {
                id: "coffee",
                name: "Coffee Break with Amy",
                cost: 5,
                type: "temporary",
                effectValue: 1.5,
                duration: 30,
                timeLeft: 0,
                isPurchased: false,
                description: "50% faster for 30 seconds",
                locked: true,
                oneTime: false
            },
            {
                id: "spreadsheet_tips",
                name: "Spreadsheet Tips from Amy",
                cost: 15,
                type: "multiplier",
                effectValue: 1.2,
                isPurchased: false,
                description: "20% permanent speed boost",
                locked: true,
                oneTime: true
            },
            {
                id: "desk_plant",
                name: "Desk Plant",
                cost: 25,
                type: "multiplier",
                effectValue: 1.15,
                isPurchased: false,
                description: "15% permanent productivity boost",
                locked: true,
                oneTime: true
            },
            {
                id: "ergonomic_chair",
                name: "Ergonomic Chair",
                cost: 50,
                type: "multiplier",
                effectValue: 1.25,
                isPurchased: false,
                description: "25% permanent comfort boost",
                locked: true,
                oneTime: true
            },
            {
                id: "energy_drink",
                name: "Energy Drink",
                cost: 10,
                type: "temporary",
                effectValue: 2.0,
                duration: 20,
                timeLeft: 0,
                isPurchased: false,
                description: "100% faster for 20 seconds",
                locked: true,
                oneTime: false
            },
            {
                id: "lunch_break",
                name: "Team Lunch",
                cost: 30,
                type: "reputation",
                effectValue: 5,
                isPurchased: false,
                description: "Gain 5 reputation points",
                locked: true,
                oneTime: false
            },
            // Auto-clicker upgrades (boost passive work rate)
            {
                id: "auto_clicker_1",
                name: "Caffeinated Mouse",
                cost: 25,
                type: "auto_clicker",
                effectValue: 0.1,
                isPurchased: false,
                description: "+0.1 work per second",
                locked: false,
                oneTime: true
            },
            {
                id: "auto_clicker_2",
                name: "Macro Keyboard",
                cost: 75,
                type: "auto_clicker",
                effectValue: 0.2,
                isPurchased: false,
                description: "+0.2 work per second",
                locked: true,
                oneTime: true
            },
            {
                id: "auto_clicker_3",
                name: "Scripted Workflow",
                cost: 200,
                type: "auto_clicker",
                effectValue: 0.4,
                isPurchased: false,
                description: "+0.4 work per second",
                locked: true,
                oneTime: true
            },
            {
                id: "auto_clicker_4",
                name: "AI Assistant",
                cost: 500,
                type: "auto_clicker",
                effectValue: 0.8,
                isPurchased: false,
                description: "+0.8 work per second",
                locked: true,
                oneTime: true
            },
            // Email auto-responder
            {
                id: "email_autoresponder",
                name: "Email Auto-Responder",
                cost: 40,
                type: "email_auto",
                effectValue: 1,
                isPurchased: false,
                description: "Auto-starts email tasks",
                locked: true,
                oneTime: true
            },
            // Task auto-starter (unlocks after auto-responder)
            {
                id: "task_scheduler",
                name: "Task Scheduler",
                cost: 150,
                type: "task_auto",
                effectValue: 1,
                isPurchased: false,
                description: "Auto-starts any available task",
                locked: true,
                oneTime: true
            }
        ];

        // Start first story event
        this.queueNextDialogue();
    }

    queueNextDialogue() {
        // Find the next eligible event that hasn't been completed yet
        // Events can be triggered out of order based on their requirements
        for (let i = 0; i < this.storyEvents.length; i++) {
            const event = this.storyEvents[i];
            // Skip events that are already completed or in the queue
            if (this.completedStoryEvents && this.completedStoryEvents.has(event.id)) {
                continue;
            }
            if (this.dialogueQueue.some(e => e.id === event.id)) {
                continue;
            }
            // Check if this event's requirements are met
            if (this.checkEventRequirements(event)) {
                this.dialogueQueue.push(event);
                return true;
            }
        }
        return false;
    }

    checkEventRequirements(event) {
        if (!event.requirement) return true;

        if (event.requirement.tasksCompleted) {
            return this.stats.tasksCompleted >= event.requirement.tasksCompleted;
        }
        if (event.requirement.taskCompletions) {
            const taskCompletions = this.stats.taskCompletions || {};
            return Object.entries(event.requirement.taskCompletions).every(([taskId, count]) =>
                (taskCompletions[taskId] || 0) >= count
            );
        }
        if (event.requirement.coffeeTaken) {
            return this.stats.coffeeTaken > 0;
        }
        if (event.requirement.taskUnlocked) {
            const task = this.tasks.find(t => t.id === event.requirement.taskUnlocked);
            return task && !task.locked;
        }
        if (event.requirement.prestige !== undefined) {
            return this.prestige.level >= event.requirement.prestige;
        }
        if (event.requirement.politicsIntroSeen) {
            return this.completedStoryEvents.has('politics_intro');
        }
        return true;
    }

    makeDialogueChoice(choiceIndex) {
        if (!this.currentDialogue || this.dialogueHandled) {
            return;
        }

        const choice = this.currentDialogue.choices[choiceIndex];

        // Check for game end FIRST before any state changes
        // This ensures gameComplete is set even if something goes wrong later
        if (choice.effect?.endGame) {
            this.gameComplete = true;
        }

        // Apply effects
        if (choice.effect) {
            if (choice.effect.hrManager !== undefined) {
                this.relationships.hrManager += choice.effect.hrManager;
            }
            if (choice.effect.supervisor !== undefined) {
                this.relationships.supervisor += choice.effect.supervisor;
            }
            if (choice.effect.colleagues) {
                Object.entries(choice.effect.colleagues).forEach(([colleague, value]) => {
                    this.relationships.colleagues[colleague] = (this.relationships.colleagues[colleague] || 0) + value;
                });
            }
            // Handle unlocks in the choice effect
            if (choice.effect.unlocks) {
                if (choice.effect.unlocks === 'coffee') {
                    const coffeeUpgrade = this.upgrades.find(u => u.id === "coffee");
                    if (coffeeUpgrade) {
                        coffeeUpgrade.locked = false;
                    }
                } else if (choice.effect.unlocks === 'spreadsheet') {
                    const spreadsheetTask = this.tasks.find(t => t.id === "spreadsheet");
                    const spreadsheetTips = this.upgrades.find(u => u.id === "spreadsheet_tips");
                    if (spreadsheetTask) {
                        spreadsheetTask.locked = false;
                    }
                    if (spreadsheetTips) {
                        spreadsheetTips.locked = false;
                    }
                }
            }
        }

        // Handle unlocks
        if (this.currentDialogue.unlocks) {
            this.processUnlocks(this.currentDialogue.unlocks);
        }
        if (choice.effect?.unlocks) {
            this.processUnlocks({ type: choice.effect.unlocks });
        }

        // Mark this event as completed before clearing it
        if (this.currentDialogue && this.currentDialogue.id) {
            this.completedStoryEvents.add(this.currentDialogue.id);
        }

        // Progress story
        this.dialogueHandled = true;
        this.currentDialogue = null;
        this.storyProgress++;

        // Validate upgrade unlocks based on new story progress
        this.validateUpgradeUnlocks();

        // Queue next dialogue immediately
        this.queueNextDialogue();

        // Auto-save after dialogue choice
        this.save();
    }

    processUnlocks(unlocks) {
        const unlockType = unlocks.type || unlocks;

        // Unlock tasks by ID
        if (unlocks.task) {
            const task = this.tasks.find(t => t.id === unlocks.task);
            if (task) task.locked = false;
        }

        // Handle specific unlock types
        switch (unlockType) {
            case 'coffee':
                this.unlockUpgrade('coffee');
                break;
            case 'spreadsheet':
                this.unlockTask('spreadsheet');
                this.unlockUpgrade('spreadsheet_tips');
                break;
            case 'phone':
                this.unlockTask('phone');
                break;
            case 'meeting':
                this.unlockTask('meeting');
                break;
            case 'training':
                this.unlockTask('training');
                break;
            case 'report':
                this.unlockTask('report');
                break;
            case 'desk_plant':
                this.unlockUpgrade('desk_plant');
                break;
            case 'ergonomic_chair':
                this.unlockUpgrade('ergonomic_chair');
                break;
            case 'energy_drink':
                this.unlockUpgrade('energy_drink');
                break;
            case 'lunch_break':
                this.unlockUpgrade('lunch_break');
                break;
        }
    }

    unlockTask(taskId) {
        const task = this.tasks.find(t => t.id === taskId);
        if (task) task.locked = false;
    }

    unlockUpgrade(upgradeId) {
        const upgrade = this.upgrades.find(u => u.id === upgradeId);
        if (upgrade) upgrade.locked = false;
    }

    // Ensure upgrades are properly unlocked based on game progress
    // This is a safety net for saves where unlock states may be inconsistent
    validateUpgradeUnlocks() {
        const tasksCompleted = this.stats.tasksCompleted;

        // Unlock upgrades based on story progress milestones
        // coffee_intro triggers at 2 tasks - coffee should be available
        if (this.storyProgress >= 2 || tasksCompleted >= 3) {
            this.unlockUpgrade('coffee');
        }

        // after_coffee triggers after first coffee - spreadsheet task and tips should be available
        // storyProgress 5 means we've moved past after_coffee dialogue
        if (this.stats.coffeeTaken > 0 && this.storyProgress >= 5) {
            this.unlockTask('spreadsheet');
            this.unlockUpgrade('spreadsheet_tips');
        }

        // desk_upgrade triggers at 5 tasks - desk_plant should be available
        if (this.storyProgress >= 5 || tasksCompleted >= 6) {
            this.unlockUpgrade('desk_plant');
        }

        // energy_drink triggers at 7 tasks
        if (this.storyProgress >= 7 || tasksCompleted >= 8) {
            this.unlockUpgrade('energy_drink');
        }

        // chair_upgrade triggers at 9 tasks
        if (this.storyProgress >= 9 || tasksCompleted >= 10) {
            this.unlockUpgrade('ergonomic_chair');
        }

        // lunch_intro triggers at 11 tasks
        if (this.storyProgress >= 11 || tasksCompleted >= 12) {
            this.unlockUpgrade('lunch_break');
        }
    }

    update() {
        const now = Date.now();
        const deltaTime = (now - this.lastUpdate) / 1000;
        this.lastUpdate = now;

        // Check for pending dialogue
        if (!this.currentDialogue && this.dialogueHandled && this.dialogueQueue.length > 0) {
            this.currentDialogue = this.dialogueQueue.shift();
            this.dialogueHandled = false;
        }

        // Retry queuing dialogue if none active and queue is empty
        // This handles cases where requirements weren't met earlier but are now
        if (!this.currentDialogue && this.dialogueHandled && this.dialogueQueue.length === 0) {
            if (this.queueNextDialogue()) {
                this.currentDialogue = this.dialogueQueue.shift();
                this.dialogueHandled = false;
            }
        }

        // Recovery: Handle orphaned state where no dialogue exists but dialogueHandled is false
        // This can occur if makeDialogueChoice() returns early due to state mismatch
        if (!this.currentDialogue && !this.dialogueHandled && this.dialogueQueue.length === 0) {
            this.dialogueHandled = true;
        }

        // Update temporary upgrade timers
        this.upgrades.forEach(upgrade => {
            if (upgrade.type === 'temporary' && upgrade.timeLeft > 0) {
                upgrade.timeLeft -= deltaTime;
                if (upgrade.timeLeft <= 0) {
                    upgrade.timeLeft = 0;
                    upgrade.isPurchased = false;
                    // Reset the multiplier by dividing out the effect
                    this.productivityMultiplier /= upgrade.effectValue;
                }
            }
        });

        // Base passive work progress (idle progress without clicking)
        // Work happens slowly on its own - clicking and upgrades just make it faster
        if (this.currentTask) {
            const basePassiveRate = 0.2; // 0.2 work per second base rate
            this.resources.workInProgress += basePassiveRate * deltaTime * this.productivityMultiplier * this.prestige.permanentBonuses.clickMultiplier;
        }

        // Process auto-clicker (adds to passive progress) - only if both enabled and active
        if (this.autoClicker.enabled && this.autoClicker.active && this.autoClicker.clicksPerSecond > 0 && this.currentTask) {
            const autoClicks = this.autoClicker.clicksPerSecond * deltaTime;
            this.resources.workInProgress += autoClicks * this.productivityMultiplier * this.prestige.permanentBonuses.clickMultiplier;
        }

        // Process employee automation
        this.processEmployeeWork(deltaTime);

        // Email auto-responder: auto-start email task if no current task (only if active)
        if (this.emailAutoResponder.enabled && this.emailAutoResponder.active && !this.currentTask) {
            const emailTask = this.tasks.find(t => t.id === 'email' && !t.locked);
            if (emailTask) {
                this.startTask('email');
            }
        }

        // Task scheduler: auto-start any available task if no current task (only if active)
        const taskScheduler = this.upgrades.find(u => u.id === 'task_scheduler' && u.isPurchased);
        if (taskScheduler && this.taskSchedulerActive && !this.currentTask) {
            const availableTask = this.tasks.find(t => !t.locked);
            if (availableTask) {
                this.startTask(availableTask.id);
            }
        }

        // Check if current task is complete (from auto-clicker or manual clicks)
        if (this.currentTask) {
            if (this.resources.workInProgress >= this.currentTask.workNeeded) {
                this.completeTask();
            }
        }

        // Check for promotion eligibility
        this.checkPromotionEligibility();

        // Enable politics after reaching Team Lead
        if (this.prestige.level >= 3 && !this.politics.enabled) {
            this.politics.enabled = true;
            // Trigger first dilemma shortly after politics is enabled
            this.politics.dilemmaTimer = 5;  // 5 seconds until first dilemma
        }

        // Process dilemma timer when politics is enabled
        if (this.politics.enabled && !this.politics.currentDilemma && !this.politics.gameOver) {
            this.politics.dilemmaTimer -= deltaTime;
            if (this.politics.dilemmaTimer <= 0) {
                this.triggerRandomDilemma();
            }
        }

        // Faction drift: at high prestige, factions slowly decay toward 50 (neutral)
        // This prevents players from just getting all factions high and coasting
        if (this.politics.enabled && !this.politics.gameOver && this.prestige.level >= 6) {
            const driftRate = 0.3 * (this.prestige.level - 5) * deltaTime; // Stronger at higher levels
            Object.keys(this.politics.factions).forEach(key => {
                const faction = this.politics.factions[key];
                if (faction.favor > 50) {
                    faction.favor = Math.max(50, faction.favor - driftRate);
                } else if (faction.favor < 50 && faction.favor >= 20) {
                    faction.favor = Math.min(50, faction.favor + driftRate * 0.5); // Drift up slower
                }
            });
        }
    }

    click() {
        if (!this.currentTask) return;

        // Add work progress from click (apply prestige click multiplier)
        const baseClickValue = 1;
        this.resources.workInProgress += baseClickValue * this.productivityMultiplier * this.prestige.permanentBonuses.clickMultiplier;

        // Check if task is complete
        if (this.resources.workInProgress >= this.currentTask.workNeeded) {
            this.completeTask();
        }
    }

    // Toggle methods for automation controls
    toggleAutoClicker() {
        this.autoClicker.active = !this.autoClicker.active;
        this.save();
        return this.autoClicker.active;
    }

    toggleEmailAutoResponder() {
        this.emailAutoResponder.active = !this.emailAutoResponder.active;
        this.save();
        return this.emailAutoResponder.active;
    }

    toggleTaskScheduler() {
        this.taskSchedulerActive = !this.taskSchedulerActive;
        this.save();
        return this.taskSchedulerActive;
    }

    // Check if any automation features are available (purchased)
    hasAutomationFeatures() {
        const hasAutoClicker = this.autoClicker.enabled;
        const hasEmailAuto = this.emailAutoResponder.enabled;
        const hasTaskScheduler = this.upgrades.find(u => u.id === 'task_scheduler' && u.isPurchased);
        return hasAutoClicker || hasEmailAuto || hasTaskScheduler;
    }

    startTask(taskId) {
        if (this.currentTask) return false;

        const task = this.tasks.find(t => t.id === taskId);
        if (task && !task.locked) {
            this.currentTask = {...task};
            this.resources.workInProgress = 0;
            return true;
        }
        return false;
    }

    completeTask() {
        if (!this.currentTask) return;

        // Store task ID before clearing it
        const completedTaskId = this.currentTask.id;

        // Apply prestige multipliers to rewards
        this.resources.money += this.currentTask.reward * this.prestige.permanentBonuses.moneyMultiplier;
        this.resources.reputation += 1 * this.prestige.permanentBonuses.reputationMultiplier;
        
        // Update stats
        this.stats.tasksCompleted++;
        this.stats.taskCompletions[completedTaskId] = 
            (this.stats.taskCompletions[completedTaskId] || 0) + 1;

        // Handle task-specific progression
        if (completedTaskId === 'email') {
            const filingTask = this.tasks.find(t => t.id === 'filing');
            if (filingTask) {
                filingTask.locked = false;
            }
        } else if (completedTaskId === 'filing') {
            // After filing, trigger the filing_complete dialogue
            this.dialogueHandled = true;
            this.storyProgress = this.storyEvents.findIndex(e => e.id === 'filing_complete');
            this.queueNextDialogue();
        }

        // Check for spreadsheet completion
        if (completedTaskId === 'spreadsheet') {
            const spreadsheetCompleteIndex = this.storyEvents.findIndex(e => e.id === 'spreadsheet_complete');
            if (spreadsheetCompleteIndex !== -1) {
                this.dialogueHandled = true;
                this.storyProgress = spreadsheetCompleteIndex;
                this.queueNextDialogue();
            }
        }
        
        // Reset task state
        this.resources.workInProgress = 0;
        this.currentTask = null;

        // Check achievements
        this.checkAchievements();

        // Auto-save after task completion
        this.save();
    }

    checkProgressionTriggers() {
        // Check for task-based dialogue triggers
        if (this.stats.tasksCompleted === 2) {
            const coffeeIntroIndex = this.storyEvents.findIndex(e => e.id === 'coffee_intro');
            if (coffeeIntroIndex !== -1 && this.storyProgress < coffeeIntroIndex) {
                this.storyProgress = coffeeIntroIndex;
                this.dialogueHandled = false;
                this.queueNextDialogue();
            }
        }
    }

    buyUpgrade(upgradeId) {
        const upgrade = this.upgrades.find(u => u.id === upgradeId);
        
        if (!upgrade || upgrade.locked || 
            (upgrade.oneTime && upgrade.isPurchased) || 
            this.resources.money < upgrade.cost) {
            return false;
        }

        this.resources.money -= upgrade.cost;
        upgrade.isPurchased = true;

        if (upgrade.type === "multiplier") {
            this.productivityMultiplier *= upgrade.effectValue;
        } else if (upgrade.type === "temporary") {
            upgrade.timeLeft = upgrade.duration;
            this.productivityMultiplier *= upgrade.effectValue;
            if (upgrade.id === 'coffee') {
                this.stats.coffeeTaken++;
                this.checkAchievements();

                // Trigger after-coffee dialogue on first coffee
                if (this.stats.coffeeTaken === 1) {
                    this.dialogueHandled = true;
                    this.storyProgress = this.storyEvents.findIndex(e => e.id === 'after_coffee');
                    if (this.storyProgress !== -1) {
                        this.queueNextDialogue();
                    }
                }
            }
            // Reset isPurchased for reusable temporary upgrades
            if (!upgrade.oneTime) {
                // Timer reset is handled in update() when timeLeft reaches 0
            }
        } else if (upgrade.type === "reputation") {
            this.resources.reputation += upgrade.effectValue;
            // Reputation upgrades can be purchased multiple times
            if (!upgrade.oneTime) {
                upgrade.isPurchased = false;
            }
        } else if (upgrade.type === "auto_clicker") {
            this.autoClicker.enabled = true;
            this.autoClicker.clicksPerSecond += upgrade.effectValue;
            this.autoClicker.level++;
        } else if (upgrade.type === "email_auto") {
            this.emailAutoResponder.enabled = true;
            this.emailAutoResponder.level++;
        } else if (upgrade.type === "task_auto") {
            // Task scheduler effect is checked in update()
        }

        // Auto-save after upgrade purchase
        this.save();

        return true;
    }

    checkAchievements() {
        const newAchievements = [];

        this.achievementsList.forEach(achievement => {
            if (this.achievements.has(achievement.id)) return;

            let earned = false;
            switch (achievement.id) {
                case "first_task":
                    earned = this.stats.tasksCompleted >= 1;
                    break;
                case "busy_worker":
                    earned = this.stats.tasksCompleted >= 5;
                    break;
                case "social_butterfly":
                    earned = this.stats.coffeeTaken >= 1;
                    break;
                case "team_player":
                    earned = (this.stats.taskCompletions['spreadsheet'] || 0) >= 1;
                    break;
                case "phone_pro":
                    earned = (this.stats.taskCompletions['phone'] || 0) >= 3;
                    break;
                case "meeting_master":
                    earned = (this.stats.taskCompletions['meeting'] || 0) >= 2;
                    break;
                case "report_writer":
                    earned = (this.stats.taskCompletions['report'] || 0) >= 1;
                    break;
                case "trained_employee":
                    earned = (this.stats.taskCompletions['training'] || 0) >= 1;
                    break;
                case "workaholic":
                    earned = this.stats.tasksCompleted >= 10;
                    break;
                case "office_veteran":
                    earned = this.stats.tasksCompleted >= 15;
                    break;
                case "money_maker":
                    earned = this.resources.money >= 100;
                    break;
                case "respected":
                    earned = this.resources.reputation >= 10;
                    break;
            }

            if (earned) {
                this.achievements.add(achievement.id);
                this.resources.money += achievement.reward;
                newAchievements.push(achievement);
            }
        });

        return newAchievements;
    }

    // Employee System
    processEmployeeWork(deltaTime) {
        if (!this.employees || !Array.isArray(this.employees) || this.employees.length === 0) {
            return;
        }

        // Calculate manager bonus
        const managers = this.employees.filter(e => e && e.type === 'manager');
        const managerBonus = 1 + (managers.length * 0.2);

        // Each employee can work on tasks independently
        this.employees.forEach((employee) => {
            if (!employee || employee.type === 'manager') {
                return; // Skip invalid entries and managers (they don't work directly)
            }

            if (!employee.currentTask) {
                if (!this.tasks || !Array.isArray(this.tasks)) {
                    return;
                }

                // Assign an available task
                const availableTask = this.tasks.find(t => !t.locked &&
                    !this.employees.some(e => e.currentTask === t.id) &&
                    this.currentTask?.id !== t.id);
                if (availableTask) {
                    employee.currentTask = availableTask.id;
                    employee.workProgress = 0;
                }
            }

            if (employee.currentTask) {
                const task = this.tasks.find(t => t.id === employee.currentTask);
                if (task) {
                    const efficiency = employee.efficiency * managerBonus *
                        this.prestige.permanentBonuses.employeeEfficiency;

                    employee.workProgress += deltaTime * efficiency;

                    if (employee.workProgress >= task.workNeeded) {
                        // Employee completed task
                        const completedTaskId = employee.currentTask;
                        const moneyReward = task.reward * this.prestige.permanentBonuses.moneyMultiplier;
                        this.resources.money += moneyReward;
                        this.resources.reputation += 1 * this.prestige.permanentBonuses.reputationMultiplier;
                        this.stats.tasksCompleted++;
                        this.stats.taskCompletions[completedTaskId] =
                            (this.stats.taskCompletions[completedTaskId] || 0) + 1;

                        // Handle task-specific progression (same as player completing tasks)
                        if (completedTaskId === 'email') {
                            const filingTask = this.tasks.find(t => t.id === 'filing');
                            if (filingTask) filingTask.locked = false;
                        } else if (completedTaskId === 'filing') {
                            this.dialogueHandled = true;
                            this.storyProgress = this.storyEvents.findIndex(e => e.id === 'filing_complete');
                            this.queueNextDialogue();
                        } else if (completedTaskId === 'spreadsheet') {
                            const spreadsheetCompleteIndex = this.storyEvents.findIndex(e => e.id === 'spreadsheet_complete');
                            if (spreadsheetCompleteIndex !== -1) {
                                this.dialogueHandled = true;
                                this.storyProgress = spreadsheetCompleteIndex;
                                this.queueNextDialogue();
                            }
                        }

                        // Reset employee
                        employee.currentTask = null;
                        employee.workProgress = 0;

                        this.checkAchievements();
                        this.save();
                    }
                } else {
                    // Task not found, reset employee
                    employee.currentTask = null;
                    employee.workProgress = 0;
                }
            }
        });
    }

    hireEmployee(typeId) {
        const employeeType = this.employeeTypes.find(t => t.id === typeId);
        if (!employeeType) {
            return false;
        }

        // Cost scales with number of employees of this type
        const existingCount = this.employees.filter(e => e.type === typeId).length;
        const cost = Math.floor(employeeType.baseCost * Math.pow(1.5, existingCount));

        if (this.resources.money < cost) {
            return false;
        }

        this.resources.money -= cost;
        const newEmployee = {
            id: `${typeId}_${Date.now()}`,
            type: typeId,
            name: employeeType.name,
            efficiency: employeeType.taskEfficiency,
            currentTask: null,
            workProgress: 0
        };
        this.employees.push(newEmployee);

        // Unlock email autoresponder after first employee
        if (this.employees.length === 1) {
            this.unlockUpgrade('email_autoresponder');
        }

        // Unlock task scheduler after 3 employees
        if (this.employees.length >= 3) {
            this.unlockUpgrade('task_scheduler');
        }

        this.save();
        return true;
    }

    getEmployeeCost(typeId) {
        const employeeType = this.employeeTypes.find(t => t.id === typeId);
        if (!employeeType) return 0;
        const existingCount = this.employees.filter(e => e.type === typeId).length;
        return Math.floor(employeeType.baseCost * Math.pow(1.5, existingCount));
    }

    // Prestige/Promotion System
    checkPromotionEligibility() {
        const totalEarned = this.resources.money + this.stats.tasksCompleted * 10;

        for (let i = this.promotionTitles.length - 1; i >= 0; i--) {
            const promotion = this.promotionTitles[i];
            if (totalEarned >= promotion.requirement && this.prestige.level < promotion.level) {
                // Player is eligible for this level but hasn't reached it
                return promotion;
            }
        }
        return null;
    }

    canPromote() {
        const nextLevel = this.prestige.level + 1;
        const nextPromotion = this.promotionTitles.find(p => p.level === nextLevel);
        if (!nextPromotion) return false;

        const totalEarned = this.resources.money + this.stats.tasksCompleted * 10;
        return totalEarned >= nextPromotion.requirement;
    }

    getNextPromotionRequirement() {
        const nextLevel = this.prestige.level + 1;
        const nextPromotion = this.promotionTitles.find(p => p.level === nextLevel);
        return nextPromotion ? nextPromotion.requirement : null;
    }

    promote() {
        if (!this.canPromote()) return false;

        const nextLevel = this.prestige.level + 1;
        const nextPromotion = this.promotionTitles.find(p => p.level === nextLevel);

        // Calculate bonuses based on current stats before reset
        const bonusMultiplier = 1 + (this.stats.tasksCompleted * 0.01);

        // Apply permanent bonuses
        this.prestige.permanentBonuses.clickMultiplier *= bonusMultiplier;
        this.prestige.permanentBonuses.moneyMultiplier *= (1 + (nextLevel * 0.1));
        this.prestige.permanentBonuses.reputationMultiplier *= (1 + (nextLevel * 0.05));
        this.prestige.permanentBonuses.employeeEfficiency *= (1 + (nextLevel * 0.05));

        // Update prestige level
        this.prestige.level = nextLevel;
        this.prestige.title = nextPromotion.title;
        this.prestige.totalPromotions++;

        // Soft reset - keep some progress
        this.resources.money = 10 * nextLevel; // Start with some money
        this.resources.workInProgress = 0;

        // Keep employees but reset their progress
        this.employees.forEach(e => {
            e.currentTask = null;
            e.workProgress = 0;
        });

        // Reset current task
        this.currentTask = null;

        // Unlock next auto-clicker tier
        if (nextLevel >= 1) this.unlockUpgrade('auto_clicker_2');
        if (nextLevel >= 2) this.unlockUpgrade('auto_clicker_3');
        if (nextLevel >= 4) this.unlockUpgrade('auto_clicker_4');

        // Check for promotion-triggered story events (like CEO victory)
        // For CEO victory (prestige 10), directly set up the dialogue to ensure it shows
        if (this.prestige.level === 10) {
            const ceoVictoryEvent = this.storyEvents.find(e => e.id === 'ceo_victory');
            if (ceoVictoryEvent && !this.completedStoryEvents.has('ceo_victory')) {
                // Clear any existing dialogue state and show CEO victory immediately
                this.dialogueQueue = [];
                this.currentDialogue = ceoVictoryEvent;
                this.dialogueHandled = false;
            }
        } else {
            this.queueNextDialogue();
        }

        this.save();
        return true;
    }

    // Politics System
    canStartScheme(schemeId) {
        const scheme = this.politicalSchemes.find(s => s.id === schemeId);
        if (!scheme) return false;
        if (this.politics.completedSchemes.includes(schemeId)) return false;
        if (this.resources.money < scheme.cost) return false;

        // Check requirements
        const req = scheme.requirement;
        if (req.reputation && this.resources.reputation < req.reputation) return false;
        if (req.influence && this.resources.influence < req.influence) return false;
        if (req.employees && this.employees.length < req.employees) return false;
        if (req.prestige && this.prestige.level < req.prestige) return false;

        return true;
    }

    startScheme(schemeId) {
        if (!this.canStartScheme(schemeId)) return false;

        const scheme = this.politicalSchemes.find(s => s.id === schemeId);
        this.resources.money -= scheme.cost;

        // Complete scheme immediately for now (could add progress later)
        this.resources.influence += scheme.influenceGain;
        this.politics.factions[scheme.faction].favor += 10;
        this.politics.completedSchemes.push(schemeId);
        this.politics.powerPlays++;

        this.save();
        return true;
    }

    getFactionStanding(factionId) {
        const faction = this.politics.factions[factionId];
        if (!faction) return 'Unknown';

        const favor = faction.favor;
        if (favor >= 80) return 'Allied';
        if (favor >= 60) return 'Friendly';
        if (favor >= 40) return 'Neutral';
        if (favor >= 20) return 'Wary';
        return 'Hostile';
    }

    // Dilemma System Methods
    getAvailableDilemmas() {
        return this.politicalDilemmas.filter(d =>
            !this.politics.completedDilemmas.includes(d.id) &&
            d.minPrestige <= this.prestige.level
        );
    }

    triggerRandomDilemma() {
        if (this.politics.currentDilemma || !this.politics.enabled) return false;

        let available = this.getAvailableDilemmas();

        // Dilemma recycling: when all dilemmas at current prestige are exhausted,
        // reset completed list and re-offer them with escalated stakes
        if (available.length === 0) {
            const allAtPrestige = this.politicalDilemmas.filter(d => d.minPrestige <= this.prestige.level);
            if (allAtPrestige.length === 0) return false;

            this.politics.dilemmaRound = (this.politics.dilemmaRound || 0) + 1;
            this.politics.completedDilemmas = [];
            available = this.getAvailableDilemmas();
            if (available.length === 0) return false;
        }

        // Pick a random dilemma
        const dilemma = available[Math.floor(Math.random() * available.length)];

        // On recycled rounds, escalate the effects (stakes get higher each cycle)
        const round = this.politics.dilemmaRound || 0;
        if (round > 0) {
            const escalation = 1 + (round * 0.15); // 15% harder each cycle
            const escalateDilemma = JSON.parse(JSON.stringify(dilemma));
            [escalateDilemma.choiceA, escalateDilemma.choiceB].forEach(choice => {
                if (choice.effects) {
                    Object.keys(choice.effects).forEach(key => {
                        choice.effects[key] = Math.round(choice.effects[key] * escalation);
                    });
                }
            });
            this.politics.currentDilemma = escalateDilemma;
        } else {
            this.politics.currentDilemma = dilemma;
        }

        return true;
    }

    makeDilemmaChoice(choiceKey) {
        if (!this.politics.currentDilemma) return false;

        const dilemma = this.politics.currentDilemma;
        const choice = choiceKey === 'A' ? dilemma.choiceA : dilemma.choiceB;

        // Apply effects to factions
        if (choice.effects) {
            if (choice.effects.management !== undefined) {
                this.politics.factions.management.favor += choice.effects.management;
            }
            if (choice.effects.workers !== undefined) {
                this.politics.factions.workers.favor += choice.effects.workers;
            }
            if (choice.effects.board !== undefined) {
                this.politics.factions.board.favor += choice.effects.board;
            }
        }

        // Clamp faction favor between 0 and 100
        Object.keys(this.politics.factions).forEach(key => {
            this.politics.factions[key].favor = Math.max(0, Math.min(100, this.politics.factions[key].favor));
        });

        // Gain influence for making political decisions
        this.resources.influence += 5;

        // Mark dilemma as completed
        this.politics.completedDilemmas.push(dilemma.id);
        this.politics.currentDilemma = null;
        this.politics.powerPlays++;

        // Reset timer for next dilemma
        this.politics.dilemmaTimer = this.politics.dilemmaInterval;

        // Check for faction failure
        this.checkFactionFailure();

        this.save();
        return true;
    }

    checkFactionFailure() {
        const factions = this.politics.factions;

        // Track faction warnings for UI
        this.politics.factionWarnings = {};

        // Warning zone: 20-30 favor triggers a warning before game over
        Object.entries(factions).forEach(([id, faction]) => {
            if (faction.favor >= 20 && faction.favor < 30) {
                this.politics.factionWarnings[id] = true;
            }
        });

        // Check if any faction dropped below 20
        const failedFaction = Object.entries(factions).find(([, f]) => f.favor < 20);
        if (!failedFaction) return false;

        const [factionId] = failedFaction;

        // Influence-based recovery: spend influence to save your career (once per faction)
        const recoveryCost = 50 + (this.prestige.level * 10);
        const recoveryKey = `recovered_${factionId}`;
        if (this.resources.influence >= recoveryCost && !this.politics[recoveryKey]) {
            this.resources.influence -= recoveryCost;
            this.politics.factions[factionId].favor = 25; // Reset to just above danger
            this.politics[recoveryKey] = true;
            this.politics.lastRecovery = {
                faction: this.politics.factions[factionId].name,
                cost: recoveryCost
            };
            return false; // Saved!
        }

        // No recovery available — game over
        const reasons = {
            management: "Management has lost confidence in you. Your career at this company is over.",
            workers: "The Workers Union has turned against you. You've been forced out.",
            board: "The Board of Directors has decided you're not CEO material. Game over."
        };
        this.politics.gameOver = true;
        this.politics.gameOverReason = reasons[factionId];
        return true;
    }

    // Check if player can still win (all factions balanced enough)
    canReachCEO() {
        const factions = this.politics.factions;
        // Need all factions at least at 20 to continue
        return factions.management.favor >= 20 &&
               factions.workers.favor >= 20 &&
               factions.board.favor >= 20;
    }

    // Save/Load System
    save() {
        const saveData = {
            version: 2,
            timestamp: Date.now(),
            resources: {
                money: this.resources.money,
                reputation: this.resources.reputation,
                influence: this.resources.influence
            },
            stats: this.stats,
            relationships: this.relationships,
            achievements: Array.from(this.achievements),
            storyProgress: this.storyProgress,
            productivityMultiplier: this.productivityMultiplier,
            gameComplete: this.gameComplete || false,
            tasks: this.tasks.map(t => ({ id: t.id, locked: t.locked })),
            upgrades: this.upgrades.map(u => ({
                id: u.id,
                locked: u.locked,
                isPurchased: u.isPurchased
            })),
            // New automation systems
            autoClicker: this.autoClicker,
            emailAutoResponder: this.emailAutoResponder,
            taskSchedulerActive: this.taskSchedulerActive,
            employees: this.employees,
            prestige: this.prestige,
            politics: this.politics,
            // Track completed story events for non-linear progression
            completedStoryEvents: Array.from(this.completedStoryEvents || [])
        };

        try {
            localStorage.setItem('officeWorkerSave', JSON.stringify(saveData));
            return true;
        } catch (e) {
            return false;
        }
    }

    load() {
        try {
            const savedData = localStorage.getItem('officeWorkerSave');
            if (!savedData) return false;

            const data = JSON.parse(savedData);

            // Restore resources
            this.resources.money = data.resources.money;
            this.resources.reputation = data.resources.reputation;
            this.resources.influence = data.resources.influence || 0;

            // Restore stats (merge with defaults for old save compatibility)
            this.stats = {
                ...this.stats,
                ...data.stats,
                taskCompletions: data.stats.taskCompletions || {}
            };

            // Restore relationships (merge with defaults for old save compatibility)
            this.relationships = {
                ...this.relationships,
                ...data.relationships,
                colleagues: data.relationships.colleagues || {}
            };

            // Restore achievements
            this.achievements = new Set(data.achievements);

            // Restore story progress
            this.storyProgress = data.storyProgress;

            // Restore multiplier
            this.productivityMultiplier = data.productivityMultiplier;

            // Restore game complete state
            this.gameComplete = data.gameComplete || false;

            // Restore task states
            data.tasks.forEach(savedTask => {
                const task = this.tasks.find(t => t.id === savedTask.id);
                if (task) {
                    task.locked = savedTask.locked;
                }
            });

            // Restore upgrade states
            data.upgrades.forEach(savedUpgrade => {
                const upgrade = this.upgrades.find(u => u.id === savedUpgrade.id);
                if (upgrade) {
                    upgrade.locked = savedUpgrade.locked;
                    upgrade.isPurchased = savedUpgrade.isPurchased;
                }
            });

            // Restore automation systems (version 2+, merge with defaults)
            if (data.autoClicker) {
                this.autoClicker = {
                    ...this.autoClicker,
                    ...data.autoClicker
                };
            }
            if (data.emailAutoResponder) {
                this.emailAutoResponder = {
                    ...this.emailAutoResponder,
                    ...data.emailAutoResponder
                };
            }
            if (data.taskSchedulerActive !== undefined) {
                this.taskSchedulerActive = data.taskSchedulerActive;
            }
            if (data.employees) {
                // Validate each employee has required properties
                this.employees = data.employees.map((emp) => ({
                    id: emp.id || `unknown_${Date.now()}`,
                    type: emp.type || 'intern',
                    name: emp.name || 'Employee',
                    efficiency: emp.efficiency ?? 0.3,
                    currentTask: emp.currentTask || null,
                    workProgress: emp.workProgress ?? 0
                }));
            }
            if (data.prestige) {
                // Merge with defaults to handle old saves missing permanentBonuses
                this.prestige = {
                    ...this.prestige,
                    ...data.prestige,
                    permanentBonuses: {
                        ...this.prestige.permanentBonuses,
                        ...(data.prestige.permanentBonuses || {})
                    }
                };
            }
            if (data.politics) {
                // Merge with defaults to handle old saves missing factions or other properties
                this.politics = {
                    ...this.politics,
                    ...data.politics,
                    factions: {
                        ...this.politics.factions,
                        ...(data.politics.factions || {})
                    },
                    completedSchemes: data.politics.completedSchemes || [],
                    completedDilemmas: data.politics.completedDilemmas || [],
                    currentDilemma: data.politics.currentDilemma || null,
                    dilemmaTimer: data.politics.dilemmaTimer ?? this.politics.dilemmaInterval,
                    dilemmaRound: data.politics.dilemmaRound || 0,
                    gameOver: data.politics.gameOver || false,
                    gameOverReason: data.politics.gameOverReason || null,
                    recovered_management: data.politics.recovered_management || false,
                    recovered_workers: data.politics.recovered_workers || false,
                    recovered_board: data.politics.recovered_board || false
                };
                // Migrate old saves that had faction favor at 0
                // New system starts at 50 (neutral)
                Object.keys(this.politics.factions).forEach(key => {
                    if (this.politics.factions[key].favor === 0 && !data.politics.completedDilemmas) {
                        this.politics.factions[key].favor = 50;
                    }
                });
            }

            // Restore completed story events for non-linear progression
            if (data.completedStoryEvents) {
                this.completedStoryEvents = new Set(data.completedStoryEvents);
            } else {
                // Backward compatibility: populate from storyProgress for old saves
                // Events before storyProgress are considered completed
                this.completedStoryEvents = new Set();
                for (let i = 0; i < this.storyProgress && i < this.storyEvents.length; i++) {
                    this.completedStoryEvents.add(this.storyEvents[i].id);
                }
            }

            // Ensure upgrades are unlocked based on story progress
            // This recovers from saves where unlocks may not have been properly recorded
            this.validateUpgradeUnlocks();

            // Clear dialogue state for clean resume
            this.currentDialogue = null;
            this.dialogueHandled = true;
            this.dialogueQueue = [];

            // Re-queue the current story event so the player can continue the story
            // This is important for dialogues like after_coffee that are triggered
            // by actions (buying coffee) rather than just task completion
            this.queueNextDialogue();
            if (this.dialogueQueue.length > 0) {
                this.currentDialogue = this.dialogueQueue.shift();
                this.dialogueHandled = false;
            }

            return true;
        } catch (e) {
            return false;
        }
    }

    hasSaveData() {
        return localStorage.getItem('officeWorkerSave') !== null;
    }

    clearSaveData() {
        localStorage.removeItem('officeWorkerSave');
    }
}

export { Resources, OfficeGame }; 